import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Alert, Card, Col, List, Row, Segmented, Space, Spin, Tag, Typography, message } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';

import { PageHeader, StatCard, StatRow } from '../../components/shared';
import {
  CityDataset,
  CityObject,
  CityObjectFeature,
  cityObjectsApi,
} from '../../api/cityObjects';

const { Text } = Typography;

const AKSAI_CENTER: [number, number] = [51.166, 53.02];
const AKSAI_ZOOM = 14;

/**
 * «Городские объекты» — то, что кнопка «Данные 2ГИС» приносит в `city_objects`.
 *
 * До этой страницы 962 объекта (107 детских площадок, парки, кладбища, рынки,
 * стадион, 336 парковок, 363 прохода) лежали в базе и не показывались НИГДЕ —
 * ни жителю, ни сотруднику. Проверить, что прогон принёс что-то осмысленное,
 * можно было только запросом в базу.
 *
 * Страница читает те же публичные ручки, что и приложение, — значит показывает
 * ровно то, что видит житель.
 */

// Русские подписи подтипов источника. Чего нет в списке — показываем как есть:
// выдумывать перевод неизвестного подтипа хуже, чем показать машинное слово.
const SUBTYPE_RU: Record<string, string> = {
  playground: 'детская площадка',
  workout_place: 'площадка для воркаута',
  sport_equipment: 'спортивные снаряды',
  outdoor_equipment: 'уличный тренажёр',
  smoking_area: 'место для курения',
  decorative_building: 'декоративный объект',
  monument: 'памятник',
  commemorative_plaque: 'памятная доска',
  fountain: 'фонтан',
  stella: 'стела',
  stage: 'сцена',
  mailbox: 'почтовый ящик',
  park: 'парк',
  railway_crossing: 'ж/д переезд',
  railway_station: 'ж/д станция',
  bus_station: 'автостанция',
  sculpture: 'скульптура',
  place: 'ориентир',
  ground: 'наземная',
  gate: 'калитка / ворота',
};

const subtypeRu = (s: string | null) => (s ? SUBTYPE_RU[s] || s : '—');

export default function CityObjects() {
  const [datasets, setDatasets] = useState<CityDataset[]>([]);
  const [dataset, setDataset] = useState<string>('places');
  const [subtype, setSubtype] = useState<string | undefined>(undefined);
  const [items, setItems] = useState<CityObject[]>([]);
  const [features, setFeatures] = useState<CityObjectFeature[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    cityObjectsApi
      .datasets()
      .then(setDatasets)
      .catch(() => message.error('Не удалось получить список наборов'));
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      cityObjectsApi.list(dataset, subtype),
      cityObjectsApi.geojson(dataset, subtype),
    ])
      .then(([list, geo]) => {
        setItems(list.items);
        setFeatures(geo.features);
      })
      .catch(() => message.error('Не удалось загрузить объекты'))
      .finally(() => setLoading(false));
  }, [dataset, subtype]);

  const current = datasets.find((d) => d.key === dataset);
  const subtypes = useMemo(
    () =>
      Object.entries(current?.subtypes || {})
        .sort((a, b) => b[1] - a[1]),
    [current]
  );
  const withPolygon = items.filter((i) => i.has_polygon).length;
  const withAddress = items.filter((i) => i.address).length;

  return (
    <div>
      <PageHeader
        title="Городские объекты"
        subtitle="Ориентиры, площадки, парковки и проходы из 2ГИС — то, что приносит кнопка «Данные 2ГИС»"
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Названия у источника родовые"
        description={
          '107 объектов называются «Детская площадка», 45 — «Спортивная площадка». ' +
          'Это не брак загрузки: такие объекты опознаются адресом и положением на карте, ' +
          'а не именем. Поэтому в списке рядом с названием стоит адрес.'
        }
      />

      <Segmented
        value={dataset}
        onChange={(v) => {
          setDataset(String(v));
          setSubtype(undefined);
        }}
        options={datasets.map((d) => ({ label: `${d.title.split(':')[0]} · ${d.count}`, value: d.key }))}
        style={{ marginBottom: 16 }}
      />

      <StatRow span={6}>
        <StatCard label="Объектов в наборе" value={current?.count ?? 0} icon={<EnvironmentOutlined />} />
        <StatCard label="Показано" value={items.length} />
        <StatCard label="С контуром (полигон)" value={withPolygon} />
        <StatCard label="С адресом" value={withAddress} />
      </StatRow>

      <Space wrap style={{ margin: '12px 0' }}>
        <Tag.CheckableTag checked={!subtype} onChange={() => setSubtype(undefined)}>
          все подтипы
        </Tag.CheckableTag>
        {subtypes.map(([key, n]) => (
          <Tag.CheckableTag
            key={key}
            checked={subtype === key}
            onChange={(c) => setSubtype(c ? key : undefined)}
          >
            {subtypeRu(key)} · {n}
          </Tag.CheckableTag>
        ))}
      </Space>

      <Row gutter={16}>
        <Col xs={24} md={16}>
          <div
            style={{
              height: 'calc(100vh - 420px)',
              minHeight: 420,
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <MapContainer center={AKSAI_CENTER} zoom={AKSAI_ZOOM} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                attribution="© OpenStreetMap · © CARTO"
              />
              <GeoJSON
                // Ключ пересобирает слой при смене набора: без него leaflet
                // оставляет на карте фигуры прошлого набора.
                key={`${dataset}-${subtype ?? 'all'}-${features.length}`}
                data={{ type: 'FeatureCollection', features } as any}
                style={() => ({ color: '#1677ff', weight: 2, fillOpacity: 0.15 })}
                pointToLayer={(_f: any, latlng: any) => {
                  // Точечные объекты (площадки, памятники) — кружком: у них
                  // `hover` в источнике формальный, рисовать контур нечего.
                  const L = require('leaflet');
                  return L.circleMarker(latlng, {
                    radius: 5,
                    color: '#1677ff',
                    fillColor: '#1677ff',
                    fillOpacity: 0.7,
                    weight: 1,
                  });
                }}
                onEachFeature={(feat: any, layer: any) => {
                  const p = feat.properties || {};
                  layer.bindTooltip(
                    `<b>${p.name || 'без названия'}</b><br/>${subtypeRu(p.subtype)}` +
                      (p.address ? `<br/>${p.address}` : ''),
                    { sticky: true }
                  );
                }}
              />
            </MapContainer>
          </div>
        </Col>

        <Col xs={24} md={8}>
          <Card size="small" title={`Список (${items.length})`} styles={{ body: { padding: 0 } }}>
            <Spin spinning={loading}>
              <div style={{ maxHeight: 'calc(100vh - 420px)', minHeight: 420, overflowY: 'auto' }}>
                <List
                  size="small"
                  dataSource={items}
                  renderItem={(o) => (
                    <List.Item>
                      <List.Item.Meta
                        title={o.name || 'без названия'}
                        description={
                          <Space size={4} wrap>
                            <Tag>{subtypeRu(o.subtype)}</Tag>
                            {o.has_polygon && <Tag>контур</Tag>}
                            {o.address && <Text type="secondary">{o.address}</Text>}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
